/**
 * Signed approval artifacts — Ed25519 signed JSON for human-in-the-loop gates.
 *
 * Used in the approval_request/approval_response message flow to create
 * tamper-evident records of owner approvals for sensitive agent operations.
 *
 * @example
 * ```typescript
 * import { createApprovalArtifact, verifyApprovalArtifact } from "@agentvault/crypto";
 *
 * const artifact = await createApprovalArtifact(
 *   { approvalId: "abc123", approved: true, reason: "Authorized" },
 *   signingPrivateKey,
 * );
 *
 * const isValid = await verifyApprovalArtifact(artifact, signerPublicKey);
 * ```
 */
import _sodium from "libsodium-wrappers-sumo";
import { canonicalize } from "./did.js";

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

export interface ApprovalArtifact {
  /** The approval content (JCS-canonicalized before signing). */
  content: Record<string, unknown>;
  /** Ed25519 signature over the canonical content (hex). */
  signature: string;
  /** Signer's Ed25519 public key (hex). */
  signerPublicKey: string;
  /** ISO 8601 timestamp of when the artifact was created. */
  createdAt: string;
  /** Version tag for forward compat. */
  version: 1;
}

/* -------------------------------------------------------------------------- */
/*  Create                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Create a signed approval artifact.
 *
 * @param content — The approval payload (e.g., { approvalId, approved, reason })
 * @param privateKeyHex — Ed25519 private key (64-byte seed+pk, hex encoded)
 * @returns Signed artifact with detached Ed25519 signature
 */
export async function createApprovalArtifact(
  content: Record<string, unknown>,
  privateKeyHex: string,
): Promise<ApprovalArtifact> {
  await _sodium.ready;
  const sodium = _sodium;

  const privateKey = sodium.from_hex(privateKeyHex);
  const publicKey = privateKey.slice(32); // Ed25519 pk is last 32 bytes of 64-byte keypair

  // Canonicalize the content for deterministic signing
  // canonicalize() returns Uint8Array (JCS-encoded bytes)
  const message = canonicalize(content);

  // Ed25519 detached signature
  const signatureBytes = sodium.crypto_sign_detached(message, privateKey);

  return {
    content,
    signature: sodium.to_hex(signatureBytes),
    signerPublicKey: sodium.to_hex(publicKey),
    createdAt: new Date().toISOString(),
    version: 1,
  };
}

/* -------------------------------------------------------------------------- */
/*  Verify                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Verify a signed approval artifact.
 *
 * @param artifact — The approval artifact to verify
 * @param expectedPublicKeyHex — Optional: verify the signer matches this key
 * @returns true if the signature is valid
 */
export async function verifyApprovalArtifact(
  artifact: ApprovalArtifact,
  expectedPublicKeyHex?: string,
): Promise<boolean> {
  await _sodium.ready;
  const sodium = _sodium;

  // Optional: verify signer identity
  if (expectedPublicKeyHex && artifact.signerPublicKey !== expectedPublicKeyHex) {
    return false;
  }

  try {
    const publicKey = sodium.from_hex(artifact.signerPublicKey);
    const signature = sodium.from_hex(artifact.signature);
    const message = canonicalize(artifact.content);

    return sodium.crypto_sign_verify_detached(signature, message, publicKey);
  } catch {
    return false;
  }
}
